import type {
  ConfirmLoaderRevUploadBody,
  ConfirmLoaderRevUploadResponse,
  ListLoaderRevUploadsResponse,
  LoaderRevUploadPreviewDto,
  RequestLoaderRevUploadResponse,
} from '@bhops/shared';
import { apiClient } from './client';

// 로더 개조 대장(엑셀) 업로드 — docs/plan/loader-rev-excel-upload-2026-09
//
// 파일은 **API 를 거치지 않는다**. 서버가 준 서명 주소로 브라우저가 S3 에 바로 PUT 한다
// (API 의 JSON 본문 상한은 2MB 이고 multipart 경로 자체가 없다).

/**
 * 브라우저의 `File.type` 이 비어 있는 환경이 있어 이 값을 **고정해서** 보낸다.
 * presigned PUT 서명에 content-type 이 들어가므로 어긋나면 S3 가 403 으로 거부한다.
 */
export const LEDGER_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' as const;

export const loaderRevUploadsApi = {
  async list(): Promise<ListLoaderRevUploadsResponse> {
    const { data } = await apiClient.get<ListLoaderRevUploadsResponse>('/loader-rev-uploads');
    return data;
  },

  async requestUpload(file: File): Promise<RequestLoaderRevUploadResponse> {
    const { data } = await apiClient.post<RequestLoaderRevUploadResponse>('/loader-rev-uploads', {
      fileName: file.name,
      contentType: LEDGER_MIME,
      byteSize: file.size,
    });
    return data;
  },

  /** S3 로 직접 PUT. 서명된 헤더를 그대로 실어야 한다(하나라도 다르면 403). */
  async putFile(
    uploadUrl: string,
    file: File,
    signedHeaders: Record<string, string>,
  ): Promise<void> {
    const headers = new Headers();
    for (const [k, v] of Object.entries(signedHeaders)) headers.set(k, v);
    let res: Response;
    try {
      res = await fetch(uploadUrl, { method: 'PUT', body: file, headers });
    } catch (err) {
      const reason = err instanceof Error ? err.message : '네트워크 오류';
      throw new Error(`파일을 올리지 못했습니다. 인터넷 연결을 확인해 주세요. (${reason})`);
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `파일을 올리지 못했습니다. 잠시 후 다시 시도해 주세요. (${res.status} ${text.slice(0, 200).replace(/\s+/g, ' ').trim()})`,
      );
    }
  },

  /** 읽고 비교만 — 이 호출은 콘솔 데이터를 바꾸지 않는다. */
  async parse(uploadId: string, baseDate?: string): Promise<LoaderRevUploadPreviewDto> {
    const { data } = await apiClient.post<LoaderRevUploadPreviewDto>(
      `/loader-rev-uploads/${uploadId}/parse`,
      baseDate ? { baseDate } : {},
      // 133대 × 개조 비교 + 엑셀 파싱 — 기본 15초로는 모자랄 수 있다.
      { timeout: 60_000 },
    );
    return data;
  },

  async confirm(
    uploadId: string,
    body: ConfirmLoaderRevUploadBody,
  ): Promise<ConfirmLoaderRevUploadResponse> {
    const { data } = await apiClient.post<ConfirmLoaderRevUploadResponse>(
      `/loader-rev-uploads/${uploadId}/confirm`,
      body,
      { timeout: 90_000 },
    );
    return data;
  },

  async cancel(uploadId: string): Promise<void> {
    await apiClient.post(`/loader-rev-uploads/${uploadId}/cancel`);
  },

  async getPreview(uploadId: string): Promise<LoaderRevUploadPreviewDto> {
    const { data } = await apiClient.get<LoaderRevUploadPreviewDto>(
      `/loader-rev-uploads/${uploadId}/preview`,
    );
    return data;
  },
};
